import React, { Fragment, useState, useEffect, useMemo, useCallback } from 'react';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Collapse, Divider, Form, message, Skeleton, Tooltip, Typography } from 'antd';
import styled from 'styled-components/macro';
import { get, cloneDeep } from 'lodash';
import YAML from 'yamljs';

import { RecipeSections, RECIPE_FIELDS, CONNECTORS_WITH_TEST_CONNECTION } from './constants';
import { RecipeField, setFieldValueOnRecipe, APPLIANCE_INGESTION } from './common';
import { XML_TYPE, XML_TYPE_PLATFORM, XML_TYPE_PLATFORM_INSTANCE } from './xml';

import TestConnectionButton from './TestConnection/TestConnectionButton';
import { SourceBuilderState, SourceConfig } from '../types';
import { jsonToYaml } from '../../utils';
import FormField from './FormField';

import { useApplianceListQuery } from '../../../../../graphql/appliance.generated';
import { useListSecretsQuery } from '../../../../../graphql/ingestion.generated';
import {
    useListDataSourcesQuery,
    useListEdcSupportedConnectorsLazyQuery,
} from '../../../../../graphql/datasource.generated';
import { dynamicConnectionFieldsMapper, updatePlatformInstanceFields } from './util_ingestion';

export const ControlsContainer = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 12px;
`;

export const StyledCollapse = styled(Collapse)`
    margin-bottom: 16px;

    .ant-collapse-header {
        font-size: 14px;
        font-weight: bold;
        padding: 12px 0;
    }
`;

const StyledSkeletonWrapper = styled.div`
    gap: 10px;
    display: flex;
    flex-direction: column;
`;

const StyledSkeletonInput = styled(Skeleton.Input)`
    height: 36px;

    &&& {
        width: 100%;
        border-radius: 4px;
    }
`;

export const StyledCollapseItem = styled(Collapse)`
    .f-header-title-no-icon {
        margin: 0;
    }
`;

const HeaderTitle = styled.span`
    margin-left: 8px;
`;

const TestConnectionWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
`;

const HeaderTooltipWrapper = styled(QuestionCircleOutlined)`
    margin-left: 5px;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
    cursor: help;
`;

const APPLIANCE_ID = 'applianceId';

function getInitialValues(displayRecipe: string, allFields: any[], applianceId, form) {
    const initialValues = {};
    let recipeObj;
    try {
        recipeObj = YAML.parse(displayRecipe);
    } catch (e) {
        message.warn('Found invalid YAML. Please check your recipe configuration.');
        return {};
    }
    if (recipeObj) {
        allFields.forEach((field) => {
            initialValues[field.name] = field.getValueFromRecipeOverride
                ? field.getValueFromRecipeOverride(recipeObj)
                : get(recipeObj, field.fieldPath);
        });
    }
    // Sets the initial value of the appliance ID
    if (applianceId) {
        form.setFieldsValue({ [APPLIANCE_ID]: applianceId });
    }

    return initialValues;
}

export function SectionHeader({ icon, text, sectionTooltip }: { icon: any; text: string; sectionTooltip?: string }) {
    return (
        <span>
            {icon}
            <HeaderTitle className="f-header-title-no-icon">{text}</HeaderTitle>
            {sectionTooltip && (
                <Tooltip placement="top" title={sectionTooltip}>
                    <HeaderTooltipWrapper />
                </Tooltip>
            )}
        </span>
    );
}

function shouldRenderFilterSectionHeader(field: RecipeField, index: number, filterFields: RecipeField[]) {
    if (index === 0 && field.section) return true;
    if (field.section && filterFields[index - 1].section !== field.section) return true;
    return false;
}

interface Props {
    state: SourceBuilderState;
    isEditing: boolean;
    displayRecipe: string;
    sourceConfigs?: SourceConfig;
    applianceId: string;
    setStagedRecipe: (recipe: string) => void;
    setApplianceId: (id: string) => void;
    setRecipeVersion: (version: string) => void;
    onClickNext: () => void;
    goToPrevious?: () => void;
}

function RecipeForm(props: Props) {
    const {
        state,
        isEditing,
        applianceId,
        displayRecipe,
        sourceConfigs,
        setStagedRecipe,
        setApplianceId,
        setRecipeVersion,
        onClickNext,
        goToPrevious,
    } = props;
    const { type } = state;
    const version = state.config?.version;
    const {
        fields,
        advancedFields,
        filterFields,
        fileLocations = [],
        filterSectionTooltip,
        advancedSectionTooltip,
        defaultOpenSections,
    } = RECIPE_FIELDS[type as string];

    const [getEdcSupportedConnectors, { data: jsonSupportedDocs, loading: jsonLoading }] =
        useListEdcSupportedConnectorsLazyQuery();

    const [connectionFields, setConnectionFields] = useState(fields);
    const [authTypeFilter, setAuthTypeFilter] = useState<string[] | null>();
    const [connectorType, setConnectorType] = useState<string | null>();
    const [isConnectorSupportedType, setIsConnectorSupportedType] = useState(false);
    const [isConnectorUpdatedVersion, setIsConnectorUpdatedVersion] = useState(false);
    // Add state to track if API has responded
    const [apiHasResponded, setApiHasResponded] = useState(false);

    useEffect(() => {
        if (type) {
            getEdcSupportedConnectors();
        }
    }, [type, getEdcSupportedConnectors]);

    useEffect(() => {
        if (jsonSupportedDocs?.listEDCSupportedConnectors) {
            const supportedConnectors = jsonSupportedDocs.listEDCSupportedConnectors;
            const connectorTypeIndex = supportedConnectors.findIndex((item: any) => item?.edc_connector_type === type);
            const isUpdatedVersion = state.recipeVersion === 'v2';
            const isSupported = connectorTypeIndex !== -1;
            const AuthTypeFilters =
                isSupported && supportedConnectors[connectorTypeIndex]?.supported_auth_types
                    ? supportedConnectors[connectorTypeIndex].supported_auth_types
                    : [];
            setIsConnectorUpdatedVersion(isUpdatedVersion);
            setIsConnectorSupportedType(isSupported);
            setAuthTypeFilter(AuthTypeFilters);
            setConnectorType(isSupported ? supportedConnectors[connectorTypeIndex]?.connector_type : null);

            if (isSupported) {
                if (isEditing && !isUpdatedVersion) {
                    setRecipeVersion('v1');
                    setConnectionFields(fields);
                } else {
                    setRecipeVersion('v2');
                    const dynamicFields = dynamicConnectionFieldsMapper(supportedConnectors[connectorTypeIndex]);
                    setConnectionFields(dynamicFields);
                }
            } else {
                setConnectionFields(fields);
            }
            setApiHasResponded(true);
        }
    }, [jsonSupportedDocs, type, fields, state.recipeVersion, isEditing, setRecipeVersion]);

    const allFields = useMemo(
        () => [...connectionFields, ...advancedFields, ...filterFields, ...fileLocations],
        [connectionFields, advancedFields, filterFields, fileLocations],
    );

    const { data, refetch: refetchSecrets } = useListSecretsQuery({
        variables: {
            input: {
                start: 0,
                count: 1000, // get all secrets
            },
        },
    });

    // This query fetch the appliance list for dropdown
    const { data: applianceData, refetch: refetchAppliance } = useApplianceListQuery({});

    // This query fetch the platform instances for the selected platform in the XML source
    const { refetch: getListDataSources } = useListDataSourcesQuery({
        skip: true,
    });

    const secrets =
        data?.listSecrets?.secrets.sort((secretA, secretB) => secretA.name.localeCompare(secretB.name)) || [];
    const [form] = Form.useForm();
    const [formErrors, setFormErrors] = useState<boolean>(true);

    // Making the appliance list format compatible with the select component.
    APPLIANCE_INGESTION.setOptions(applianceData?.applianceList);

    // Separate function to validate form state
    const validateFormState = useCallback(
        async (applianceIdOverride?: string) => {
            try {
                const requiredFields = [...allFields.filter((field) => field.required === true)];

                if (!isConnectorSupportedType || (isEditing && !isConnectorUpdatedVersion)) {
                    requiredFields.push(APPLIANCE_INGESTION);
                }

                // Force validation of all fields first - this will mark fields with errors
                try {
                    // Use validateFields without await for initial check
                    form.validateFields(requiredFields.map((field) => field.name));
                } catch (validationError) {
                    // Ignore error here - we'll check field errors separately below
                    console.error('Initial validation check found errors');
                }

                // Check for validation errors in all fields
                const hasNoErrors = form.getFieldsError().every((field) => field.errors.length === 0);

                // Check that all required fields have values
                const hasAllRequiredFields = requiredFields.every((field) => {
                    const value = form.getFieldValue(field.name);

                    // Special handling for array fields
                    if (Array.isArray(value)) {
                        // Array should not be empty for required fields
                        if (value.length === 0) {
                            return false;
                        }

                        // Special handling for path_specs array of objects
                        if (field.name === 'path_specs') {
                            return value.every((item) => item?.include?.trim() && item?.xsds_path?.trim());
                        }

                        // General handling for array of objects
                        return value.every((item) => {
                            const isObject = item && typeof item === 'object';
                            const keys = Object.keys(item);
                            return (
                                isObject &&
                                keys.length > 0 &&
                                keys.every((key) => item[key] !== undefined && item[key] !== null && item[key] !== '')
                            );
                        });
                    }

                    // Regular field value check
                    return value !== undefined && value !== null && value !== '';
                });

                // Check if appliance ID is selected
                const hasApplianceId = !!applianceId || !!applianceIdOverride;

                // Form is valid if it passes all checks
                const isValid = hasAllRequiredFields && hasApplianceId && hasNoErrors;

                // Update form errors state
                setFormErrors(!isValid);
            } catch (error) {
                setFormErrors(true);
            }
        },
        [form, allFields, applianceId, isConnectorSupportedType, isEditing, isConnectorUpdatedVersion],
    );

    useEffect(() => {
        validateFormState();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allFields, applianceId]);

    // Helper function for fetching platform instances
    async function fetchXmlPlatformInstances(platform: string) {
        try {
            if (!platform) {
                return [];
            }

            const { data: datasources } = await getListDataSources({
                input: {
                    start: 0,
                    count: 1000,
                    dsConnectorType: 'onprem',
                    connectorTypeId: [platform],
                    authType: authTypeFilter,
                },
            });

            return datasources?.listDataSources || [];
        } catch (e) {
            console.error(`Failed to fetch platform instances for ${platform}:`, e);
            return [];
        }
    }

    async function updateFormValues(changedValues: any, allValues: any) {
        let updatedValues = YAML.parse(displayRecipe);

        if (changedValues[XML_TYPE_PLATFORM]) {
            try {
                const xmlResults = await fetchXmlPlatformInstances(changedValues[XML_TYPE_PLATFORM]);

                // Create a new copy of connectionFields to avoid mutation issues
                const updatedFields = [...connectionFields];

                // Update platform instance fields using the utility function
                const newUpdatedFields = updatePlatformInstanceFields(updatedFields, XML_TYPE, xmlResults, form);
                setConnectionFields(newUpdatedFields);
            } catch (e) {
                console.error('Error fetching platform instances:', e);
            }
        }

        // Update recipe with changed values
        Object.keys(changedValues).forEach((fieldName) => {
            const recipeField = allFields.find((f) => f.name === fieldName);

            if (recipeField) {
                // Use the override function if available, otherwise use the default setter
                updatedValues = recipeField.setValueOnRecipeOverride
                    ? recipeField.setValueOnRecipeOverride(updatedValues, allValues[fieldName])
                    : setFieldValueOnRecipe(updatedValues, allValues[fieldName], recipeField.fieldPath);
            }
        });

        let tempApplianceID = '';

        if (!changedValues[XML_TYPE_PLATFORM_INSTANCE] && changedValues[`${type}.platform_instance`]) {
            const updatedFields = [...connectionFields];
            const selectedField = updatedFields.find((field) => field.name === `${type}.platform_instance`);
            const selectedFieldOption = selectedField?.options?.find(
                (option) => option.value === changedValues[`${type}.platform_instance`],
            );
            setApplianceId(selectedFieldOption?.applianceId ?? '');
            tempApplianceID = selectedFieldOption?.applianceId ?? '';
        }

        // Handle appliance ID changes
        if (changedValues[APPLIANCE_ID]) {
            setApplianceId(changedValues[APPLIANCE_ID]);
            tempApplianceID = changedValues[APPLIANCE_ID];
        }

        // Convert updated recipe to YAML and set it
        const stagedRecipe = jsonToYaml(JSON.stringify(updatedValues));
        setStagedRecipe(stagedRecipe);

        // Always validate form after values change
        // Use async/await to ensure state updates
        await validateFormState(tempApplianceID);
    }

    function updateFormValue(fieldName, fieldValue) {
        updateFormValues({ [fieldName]: fieldValue }, { [fieldName]: fieldValue });
        form.setFieldsValue({ [fieldName]: fieldValue });
    }

    useEffect(() => {
        async function fetchPlatformIntances() {
            try {
                const results = await fetchXmlPlatformInstances(connectorType ?? '');

                // Create a new copy of connectionFields to avoid mutation issues
                const updatedFields = cloneDeep(connectionFields);

                // Update platform instance fields using the utility function
                if (type) {
                    const newUpdatedFields = updatePlatformInstanceFields(updatedFields, type, results, form);
                    setConnectionFields(newUpdatedFields);
                    const val = getInitialValues(displayRecipe, allFields, applianceId, form);
                    Object.keys(val).forEach((field) => {
                        updateFormValue(field, val[field]);
                    });
                    setApplianceId(applianceId);
                }
            } catch (e) {
                console.error('Error fetching platform instances:', e);
            }
        }

        if (isConnectorSupportedType) {
            fetchPlatformIntances();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isConnectorSupportedType]);

    return (
        <Form
            layout="vertical"
            initialValues={getInitialValues(displayRecipe, allFields, applianceId, form)}
            onFinish={onClickNext}
            form={form}
            onValuesChange={updateFormValues}
        >
            {jsonLoading ? (
                <StyledSkeletonWrapper>
                    {Array.from({ length: 4 }).map(() => (
                        <>
                            <StyledSkeletonInput active />
                            <Divider className="ma-0" />
                        </>
                    ))}
                </StyledSkeletonWrapper>
            ) : (
                <>
                    {(!isConnectorSupportedType || (isEditing && !isConnectorUpdatedVersion)) && (
                        <>
                            <StyledCollapseItem className="f-expand-icon-left-align" defaultActiveKey="0">
                                <Collapse.Panel
                                    forceRender
                                    header={<SectionHeader icon={false} text="Pods" />}
                                    key="0"
                                    collapsible={apiHasResponded ? undefined : 'disabled'}
                                >
                                    {/* TODO this was showing Error Property 'updateFormValue' is missing in type  need Anas input to check this */}
                                    <FormField
                                        secrets={secrets}
                                        field={APPLIANCE_INGESTION}
                                        updateFormValue={updateFormValue}
                                        refetchSecrets={refetchAppliance}
                                    />
                                </Collapse.Panel>
                            </StyledCollapseItem>
                            <Divider className="my-2" />
                        </>
                    )}
                    <StyledCollapseItem
                        className="f-expand-icon-left-align"
                        defaultActiveKey={defaultOpenSections?.includes(RecipeSections.Connection) ? '1' : ''}
                    >
                        <Collapse.Panel
                            forceRender
                            header={<SectionHeader icon={false} text="Connection" />}
                            key="1"
                            collapsible={apiHasResponded ? undefined : 'disabled'}
                        >
                            {connectionFields.map((field, i) => (
                                <FormField
                                    key={field.name}
                                    field={field}
                                    secrets={secrets}
                                    refetchSecrets={refetchSecrets}
                                    removeMargin={i === connectionFields.length - 1}
                                    updateFormValue={updateFormValue}
                                />
                            ))}

                            {(!isConnectorSupportedType || (isEditing && !isConnectorUpdatedVersion)) &&
                                CONNECTORS_WITH_TEST_CONNECTION.has(type as string) && (
                                    <TestConnectionWrapper>
                                        <TestConnectionButton
                                            type={type || ''}
                                            applianceId={applianceId}
                                            recipe={displayRecipe}
                                            sourceConfigs={sourceConfigs}
                                            version={version}
                                            form={form}
                                        />
                                    </TestConnectionWrapper>
                                )}
                        </Collapse.Panel>
                    </StyledCollapseItem>

                    <Divider className="my-2" />

                    {filterFields.length > 0 && (
                        <>
                            <StyledCollapseItem
                                className="f-expand-icon-left-align"
                                defaultActiveKey={defaultOpenSections?.includes(RecipeSections.Filter) ? '2' : ''}
                            >
                                <Collapse.Panel
                                    forceRender
                                    header={
                                        <SectionHeader
                                            icon={false}
                                            text="Filter"
                                            sectionTooltip={filterSectionTooltip}
                                        />
                                    }
                                    key="2"
                                    collapsible={apiHasResponded ? undefined : 'disabled'}
                                >
                                    {filterFields.map((field, i) => (
                                        <Fragment key={field.name}>
                                            {shouldRenderFilterSectionHeader(field, i, filterFields) && (
                                                <Typography.Title level={4}>{field.section}</Typography.Title>
                                            )}
                                            <FormField
                                                field={field}
                                                secrets={secrets}
                                                refetchSecrets={refetchSecrets}
                                                removeMargin={i === filterFields.length - 1}
                                                updateFormValue={updateFormValue}
                                            />
                                        </Fragment>
                                    ))}
                                </Collapse.Panel>
                            </StyledCollapseItem>
                            <Divider className="my-2" />
                        </>
                    )}

                    {advancedFields.length > 0 && (
                        <>
                            <StyledCollapseItem
                                className="f-expand-icon-left-align"
                                defaultActiveKey={defaultOpenSections?.includes(RecipeSections.Advanced) ? '3' : ''}
                            >
                                <Collapse.Panel
                                    forceRender
                                    header={
                                        <SectionHeader
                                            icon={false}
                                            text="Advanced"
                                            sectionTooltip={advancedSectionTooltip}
                                        />
                                    }
                                    key="3"
                                    collapsible={apiHasResponded ? undefined : 'disabled'}
                                >
                                    {advancedFields.map((field, i) => (
                                        <FormField
                                            key={field.name}
                                            field={field}
                                            secrets={secrets}
                                            refetchSecrets={refetchSecrets}
                                            removeMargin={i === advancedFields.length - 1}
                                            updateFormValue={updateFormValue}
                                        />
                                    ))}
                                </Collapse.Panel>
                            </StyledCollapseItem>
                            <Divider className="my-2" />
                        </>
                    )}

                    {fileLocations.length > 0 && (
                        <>
                            <StyledCollapseItem
                                className="f-expand-icon-left-align"
                                defaultActiveKey={defaultOpenSections?.includes(RecipeSections.Advanced) ? '4' : ''}
                            >
                                <Collapse.Panel
                                    forceRender
                                    header={
                                        <SectionHeader
                                            icon={false}
                                            text="File Path"
                                            sectionTooltip={advancedSectionTooltip}
                                        />
                                    }
                                    key="4"
                                    collapsible={apiHasResponded ? undefined : 'disabled'}
                                >
                                    {fileLocations.map((field, i) => (
                                        <FormField
                                            key={field.name}
                                            field={field}
                                            secrets={secrets}
                                            refetchSecrets={refetchSecrets}
                                            removeMargin={i === advancedFields.length - 1}
                                            updateFormValue={updateFormValue}
                                        />
                                    ))}
                                </Collapse.Panel>
                            </StyledCollapseItem>
                            <Divider className="my-2" />
                        </>
                    )}
                </>
            )}

            <ControlsContainer>
                <Button disabled={isEditing} onClick={goToPrevious}>
                    Previous
                </Button>
                <Button disabled={formErrors} htmlType="submit">
                    Next
                </Button>
            </ControlsContainer>
        </Form>
    );
}

export default RecipeForm;
